import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import InternetBookingForm from "./InternetAgreementForm";
import MemoryDeliveryNote from "./MemoryBookDeliveryNote";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface PrintDialogProps {
  open: boolean;
  onClose: () => void;
  documentType: "internet-form" | "memory-delivery-note";
  bookingId?: number | string;
  streamId?: number;
  agreementTemplateId?: string;
}

const PrintDocument: React.FC<PrintDialogProps> = ({
  open,
  onClose,
  documentType,
  bookingId,
  streamId,
  agreementTemplateId,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const printRef = useRef<HTMLDivElement>(null);
  const preparedPdfRef = useRef<jsPDF | null>(null);
  const preparingPdfRef = useRef<Promise<jsPDF | null> | null>(null);
  const [loadingAction, setLoadingAction] = useState<
    "print" | "download" | null
  >(null);

  const documentTitle =
    documentType === "internet-form"
      ? "Livestream-Booking-Agreement-and-Client-Acknowledgement-Form"
      : "Memory-Book-Delivery-Note";

  const generatePDF = async (): Promise<jsPDF | null> => {
    if (!printRef.current) {
      toast.error("No content to generate");
      return null;
    }

    try {
      // html2canvas must measure text only after the web font is ready. Capturing
      // earlier can collapse the width of spaces while retaining word widths.
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const isInternetAgreement = documentType === "internet-form";
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: isInternetAgreement ? "letter" : "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const agreementPages = Array.from(
        printRef.current.querySelectorAll<HTMLElement>(".agreement-page"),
      );

      if (isInternetAgreement && agreementPages.length > 0) {
        const sourcePage = agreementPages[0];
        const sourceContent = sourcePage.querySelector<HTMLElement>(
          ".internet-agreement-content",
        );
        if (!sourceContent) return null;

        const staging = document.createElement("div");
        staging.style.cssText =
          "position:fixed;left:-10000px;top:0;width:8.5in;background:#fff;";
        document.body.appendChild(staging);

        const paginatedPages: HTMLElement[] = [];
        const stabilizeCanvasText = (root: HTMLElement) => {
          [root, ...Array.from(root.querySelectorAll<HTMLElement>("*"))]
            .forEach((element) => {
              // A non-zero value makes html2canvas position characters
              // individually instead of using the faulty whole-run metrics.
              // 0.01px is visually imperceptible and retains Montserrat.
              element.style.setProperty("letter-spacing", "0.01px");
            });
        };
        const createPage = () => {
          const page = sourcePage.cloneNode(true) as HTMLElement;
          page.style.height = "11in";
          page.style.minHeight = "11in";
          page.style.maxHeight = "11in";
          page.style.overflow = "hidden";
          const content = page.querySelector<HTMLElement>(
            ".internet-agreement-content",
          )!;
          content.innerHTML = "";
          content.style.height = "9in";
          content.style.overflow = "hidden";
          content.style.wordSpacing = "normal";
          content.style.letterSpacing = "normal";
          content.style.whiteSpace = "normal";
          content.style.fontFamily = '"Montserrat", Arial, sans-serif';
          staging.appendChild(page);
          paginatedPages.push(page);
          return content;
        };

        let pageContent = createPage();
        Array.from(sourceContent.children).forEach((child) => {
          const clone = child.cloneNode(true) as HTMLElement;
          stabilizeCanvasText(clone);
          pageContent.appendChild(clone);
          if (pageContent.scrollHeight > pageContent.clientHeight) {
            pageContent.removeChild(clone);
            pageContent = createPage();
            pageContent.appendChild(clone);
          }
        });

        for (let index = 0; index < paginatedPages.length; index += 1) {
          const canvas = await html2canvas(paginatedPages[index], {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            windowWidth: paginatedPages[index].scrollWidth,
            windowHeight: paginatedPages[index].scrollHeight,
          });
          if (index > 0) pdf.addPage("letter", "p");
          pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pdfWidth, pdfHeight);
        }
        document.body.removeChild(staging);
        return pdf;
      }

      const canvas = await html2canvas(printRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: printRef.current.scrollWidth,
        windowHeight: printRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

      return pdf;
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Document generation failed");
      return null;
    }
  };

  const getPreparedPdf = () => {
    if (preparedPdfRef.current) {
      return Promise.resolve(preparedPdfRef.current);
    }
    if (preparingPdfRef.current) return preparingPdfRef.current;

    preparingPdfRef.current = generatePDF()
      .then((pdf) => {
        preparedPdfRef.current = pdf;
        return pdf;
      })
      .finally(() => {
        preparingPdfRef.current = null;
      });
    return preparingPdfRef.current;
  };

  useEffect(() => {
    preparedPdfRef.current = null;
    preparingPdfRef.current = null;
    if (!open || !printRef.current) return;

    let prepareTimer: number | undefined;
    const schedulePreparation = () => {
      if (
        documentType === "internet-form" &&
        !printRef.current?.querySelector(".agreement-page")
      ) {
        return;
      }
      window.clearTimeout(prepareTimer);
      prepareTimer = window.setTimeout(() => void getPreparedPdf(), 350);
    };

    const observer = new MutationObserver(schedulePreparation);
    observer.observe(printRef.current, { childList: true, subtree: true });
    schedulePreparation();

    return () => {
      window.clearTimeout(prepareTimer);
      observer.disconnect();
    };
    // The document is deliberately regenerated only when the dialog source changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentType, bookingId, streamId]);

  const handlePrint = async () => {
    if (!printRef.current) {
      toast.error("No content to print");
      return;
    }

    setLoadingAction("print");
    try {
      const pdf = await getPreparedPdf();
      if (!pdf) return;

      const pdfUrl = URL.createObjectURL(pdf.output("blob"));
      const printFrame = document.createElement("iframe");
      printFrame.setAttribute("aria-hidden", "true");
      printFrame.style.cssText =
        "position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;";

      const cleanup = () => {
        URL.revokeObjectURL(pdfUrl);
        printFrame.remove();
      };

      await new Promise<void>((resolve, reject) => {
        printFrame.onload = () => {
          const frameWindow = printFrame.contentWindow;
          if (!frameWindow) {
            cleanup();
            reject(new Error("Print frame could not be opened"));
            return;
          }

          frameWindow.addEventListener("afterprint", cleanup, { once: true });
          window.setTimeout(() => {
            frameWindow.focus();
            frameWindow.print();
            resolve();
          }, 150);
        };
        printFrame.onerror = () => {
          cleanup();
          reject(new Error("Print document could not be loaded"));
        };
        printFrame.src = pdfUrl;
        document.body.appendChild(printFrame);
      });

      // Fallback cleanup for browsers that do not emit afterprint for frames.
      window.setTimeout(cleanup, 120000);
    } catch (error) {
      console.error("Print failed:", error);
      toast.error("Print preparation failed");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownload = async () => {
    setLoadingAction("download");
    try {
      const pdf = await getPreparedPdf();
      if (pdf) {
        pdf.save(`${documentTitle}.pdf`);
        toast.success("PDF downloaded successfully");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed");
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight="700">
            {documentType === "internet-form"
              ? "Livestream Booking Agreement Preview"
              : "Memory Delivery Note Preview"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          sx={{ mt: 1, color: reduxTheme.primary_colors?.[700] }}
        >
          Preview and print the document
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box ref={printRef}>
          {documentType === "internet-form" ? (
        <InternetBookingForm
          bookingIdOverride={bookingId}
          streamIdOverride={streamId}
          templateIdOverride={agreementTemplateId}
        />
          ) : (
            <MemoryDeliveryNote />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
        <Button
          onClick={handleDownload}
          variant="outlined"
          startIcon={
            loadingAction === "download" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          disabled={loadingAction !== null}
        >
          {loadingAction === "download" ? "Downloading..." : "Download PDF"}
        </Button>

        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={
            loadingAction === "print" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PrintIcon />
            )
          }
          disabled={loadingAction !== null}
        >
          {loadingAction === "print" ? "Preparing..." : "Print"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintDocument;
